// Derived from FiltersMixin.js

import { Timestamp } from 'firebase/firestore';
import { cloneDeep } from 'lodash';
import Mustache from 'mustache';
import { date } from 'quasar';
import { filterPropertyTypes } from 'src/boot/globals';
import { DATE_FILTER_OPERATORS } from 'src/constants/filters';
import { deepGet, filterDeep } from 'src/utils/AppUtils';

const FILTER_FUNCTION_MAP = {
  [filterPropertyTypes.ArrayContainsUser]: filterPropertyTypes.ArrayInArray,
  [filterPropertyTypes.ArrayInArray]: filterPropertyTypes.ArrayInArray,
  [filterPropertyTypes.Client]: filterPropertyTypes.StringInArray,
  [filterPropertyTypes.User]: filterPropertyTypes.StringInArray,
  [filterPropertyTypes.StringInArray]: filterPropertyTypes.StringInArray,
  [filterPropertyTypes.NullValue]: filterPropertyTypes.NullValue,
  [filterPropertyTypes.Date]: filterPropertyTypes.Date,
  [filterPropertyTypes.StringMatch]: filterPropertyTypes.StringMatch,
  [filterPropertyTypes.Duration]: filterPropertyTypes.Number,
  [filterPropertyTypes.Number]: filterPropertyTypes.Number,
  [filterPropertyTypes.Boolean]: filterPropertyTypes.Boolean,
};

/**
 * Whether a timestamp is older than a number of weeks, counted back from the start of today.
 * No usable timestamp means the client never had a session, which is as dormant as a client gets.
 *
 * @param {Timestamp} timestamp value read off the filtered element
 * @param {Number} weeks number of weeks, greater than zero
 * @returns {Boolean}
 */
export function isMoreThanWeeksAgo(timestamp, weeks) {
  if (!Number.isFinite(weeks) || weeks <= 0) {
    return false;
  }

  if (!(timestamp instanceof Timestamp)) {
    return true;
  }

  const cutoff = date.subtractFromDate(date.startOfDate(new Date(), 'day'), { days: weeks * 7 });

  return timestamp.toMillis() < cutoff.getTime();
} // isMoreThanWeeksAgo()

/**
 * @private
 * Used in {@link getFilteredElements}
 * Get matched params from element based on filter item
 * @param {Object} filterItem object with main filter info
 * @param {Object} element object from the array to be filtered
 * @returns value of filter property key from the element
 */
function getMatchedParams(filterItem, element, filterKey = null) {
  if (typeof filterItem.filterPropertyCallback === 'function') {
    return filterItem.filterPropertyCallback(element);
  }

  let { filterProperty } = filterItem;
  if (!filterProperty && filterKey) {
    /*
     * For filters saved before the report template criteria declared filterProperty. Everything
     * built today sets it, so this is only reached by definitions already stored in a template,
     * which cannot be migrated from here. It is not a general fallback to lean on: the matcher in
     * FiltersMixin, which every table filter goes through, has none, so a criterion relying on
     * this works when a report renders it and silently matches nothing anywhere else.
     */
    filterProperty = filterKey;
  }

  const matchFilterProperty = filterItem.filterObject
    ? `${filterItem.filterObject}.${filterProperty}`
    : filterProperty;

  const elementItem = deepGet(element, matchFilterProperty);

  // If the filtered array is inside an object then return array of values from that object property
  if (filterItem.arrayProperty) {
    return elementItem?.map((item) => item?.[filterItem.arrayProperty]) || [];
  }
  return elementItem;
} // getMatchedParams()

/**
 * @private
 * Used in {@link getFilteredElements}
 * @param {Array.<Object>} elements all items from component
 * @param {Array.<string> | string} filterItem array of parameters to get nested value from the element
 * @param {string} filterCriteriaType type of filter criteria to find matches all over the string
 * or at the beginning or full string match
 * @param {string} filteredItems user input
 * @returns array of items
 */
function filterElementByStringMatch(
  element,
  filterItem,
  filterKey,
  { filterCriteriaType, filteredItems, filterNegative },
) {
  if (typeof filteredItems !== 'string') {
    return false;
  }
  let prop = getMatchedParams(filterItem, element, filterKey);

  if (typeof prop === 'number') {
    prop = String(prop);
  }

  if (prop === null || typeof prop !== 'string') {
    return false;
  }

  if (prop && filterCriteriaType === 'startsWith') {
    return prop.toLowerCase().startsWith(filteredItems.toLowerCase()) !== !!filterNegative;
  }

  if (prop && filterCriteriaType === 'isEqualTo') {
    return (filteredItems.toLowerCase() === prop.toLowerCase()) !== !!filterNegative;
  }

  if (prop && filterCriteriaType === 'excludes') {
    return (!prop.toLowerCase().includes(filteredItems.toLowerCase())) !== !!filterNegative;
  }

  return prop
    ? prop.toLowerCase().includes(filteredItems.toLowerCase()) !== !!filterNegative
    : false;
} // filterElementByStringMatch()

function filterElementByNumber(
  element,
  filterItem,
  filterKey,
  { filterCriteriaType, filteredItems, filterNegative },
) {
  let prop = getMatchedParams(filterItem, element, filterKey);

  if (prop === null || prop === undefined) {
    return false;
  }
  prop = Number(prop);

  if (Number.isNaN(prop)) {
    return false;
  }

  switch (filterCriteriaType) {
    case 'lessThan':
      return (prop < filteredItems) !== !!filterNegative;
    case 'lessThanEqual':
      return (prop <= filteredItems) !== !!filterNegative;
    case 'greaterThanEqual':
      return (prop >= filteredItems) !== !!filterNegative;
    case 'greaterThan':
      return (prop > filteredItems) !== !!filterNegative;
    case 'between': {
      const from = Number(filteredItems?.from);
      const to = Number(filteredItems?.to);

      if (Number.isNaN(from) || Number.isNaN(to)) {
        return false;
      }

      const min = Math.min(from, to);
      const max = Math.max(from, to);

      return (prop >= min && prop <= max) !== !!filterNegative;
    }
    default:
      return (prop === filteredItems) !== !!filterNegative;
  }
} // filterElementByNumber()

function filterElementByBoolean(
  element,
  filterItem,
  filterKey,
  { filteredItems, filterNegative },
) {
  const prop = getMatchedParams(filterItem, element, filterKey);

  if (typeof filteredItems !== 'boolean' && filteredItems !== null) {
    return false;
  }

  if (filteredItems === null) {
    return ((prop === null || prop === undefined)) !== !!filterNegative;
  }

  return (prop === filteredItems) !== !!filterNegative;
} // filterElementByBoolean()

const FILTER_FUNCTIONS = {
  [filterPropertyTypes.StringMatch]: (elements, filterItem, filterKey) => {
    return elements.filter((element) => (
      filterItem.filterStatements?.every((filterStatement) => (
        filterElementByStringMatch(element, filterItem, filterKey, filterStatement)
      ))
    ));
  }, // StringMatch()

  [filterPropertyTypes.StringInArray]: (elements, filterItem, filterKey) => {
    return elements.filter((element) => {
      const getParams = getMatchedParams(filterItem, element, filterKey);

      return filterItem.filterStatements?.every((filterStatement) => (
        filterStatement.filteredItems.includes(getParams ?? null) !== !!filterStatement.filterNegative
      ));
    });
  }, // StringInArray()
  [filterPropertyTypes.Number]: (elements, filterItem, filterKey) => {
    return elements.filter((element) => (
      filterItem.filterStatements?.every((filterStatement) => (
        filterElementByNumber(element, filterItem, filterKey, filterStatement)
      ))
    ));
  }, // Number()
  [filterPropertyTypes.Boolean]: (elements, filterItem, filterKey) => {
    return elements.filter((element) => (
      filterItem.filterStatements?.every((filterStatement) => (
        filterElementByBoolean(element, filterItem, filterKey, filterStatement)
      ))
    ));
  }, // Boolean()

  /**
   * Filter elements where a property is an array that contains any of the values in the filter statements
   *
   * The logic behind ArrayInArray is to match AND logic for each statement and OR logic
   * inside each statement. Tags are a good example of this type of filter.
   * We can have multiple tag filters (AND logic) and each filter can have multiple
   * selected tags (OR logic)
   *
   * @param {Array} elements Array of elements to be filtered
   * @param {Object} filterItem Object with filter info
   * @returns
   */
  [filterPropertyTypes.ArrayInArray]: (elements, filterItem, filterKey) => {
    // filterStatements contains an array of each filter badge
    return elements.filter((element) => {
      const getParams = getMatchedParams(filterItem, element, filterKey) || [];

      // In case filterCriteriaItems contains an array with null value as a criteria and
      // we need to filter items that have no value (like an empty array in the Clients BU field)
      const emptyParams = !getParams?.length;
      // filteredItems from filterStatements is an array of all the ids to be search in filter badge
      return filterItem.filterStatements?.every(
        (filterStatement) => filterStatement.filteredItems
          .some((item) => (
            getParams.includes(item)
            || (emptyParams && item === null))) !== !!filterStatement.filterNegative,
      );
    });
  }, // ArrayInArray()

  [filterPropertyTypes.NullValue]: (elements, filterItem, filterKey) => {
    return elements.filter((element) => filterItem.filterStatements?.every(
      (filterStatement) => filterStatement.filteredItems
        .includes(
          (getMatchedParams(filterItem, element, filterKey) ?? null) === null,
        ) !== !!filterStatement.filterNegative,
    ));
  }, // NullValue()

  [filterPropertyTypes.Date]: (elements, filterItem, filterKey) => (
    elements.filter((element) => filterItem.filterStatements?.every(
      (filterStatement) => {
        const elementTs = getMatchedParams(filterItem, element, filterKey);

        if (filterStatement.dateOperator === DATE_FILTER_OPERATORS.IS_MORE_THAN) {
          return isMoreThanWeeksAgo(elementTs, filterStatement.filteredItems);
        }

        if (!(elementTs instanceof Timestamp)) {
          return false;
        }

        if (filterStatement.filterCriteriaType === 'custom-dates') {
          return (filterStatement.filterDateItems || [])
            .filter((dateItem) => (dateItem instanceof Timestamp))
            .some((dateItem) => (
              elementTs.seconds >= dateItem.seconds
                && elementTs.seconds <= dateItem.seconds + 86399
            ));
        }

        const { fromDate, toDate } = filterStatement;

        return (elementTs?.toMillis() >= fromDate?.toMillis() && elementTs?.toMillis() <= toDate?.toMillis());
      },
    ))
  ),
};

/**
 * Get items list according to the filters object. Recurcive
 * @param {Array.<Object>} elements all items from component or items as the result of function
 * @param {Object} elementsFilter object with filter key. contains statements for filtering
 * @returns {Array.<Object>} array of items
 */
function filterElements(elements = [], elementsFilter = null) {
  if (Array.isArray(elements) === false || elements.length === 0) {
    return elements;
  }

  return Object.keys(elementsFilter).reduce(
    (elementFilter, filterKey) => {
      const filterItem = elementsFilter[filterKey];

      const filterFunctionType = FILTER_FUNCTION_MAP[filterItem.filterPropertyType];
      if (typeof FILTER_FUNCTIONS[filterFunctionType] !== 'function') {
        throw new Error(`Unsupported filter type: ${filterItem.filterPropertyType}`);
      }
      return FILTER_FUNCTIONS[filterFunctionType](elementFilter, filterItem, filterKey);
    },
    elements,
  );
} // filterElements()

/**
 * Get filtered elements according to the filter object
 * @param {Array.<Object>} items all items from component
 * @param {Object} filterObject object with filter key. contains statements for filtering
 * @returns {Array.<Object>} array of items
 */
export function getFilteredElements(items, filterObject) {
  if (typeof filterObject !== 'object' || filterObject === null || Object.keys(filterObject).length === 0) {
    return items;
  }

  return filterElements(items, filterObject);
} // getFilteredElements()

/**
 * Filter a field value map to only include fields required by this element. Extracts
 * dynamic fields from a string and filters the field value map to only include those fields.
 * @param {String} str The string to parse for dynamic fields
 * @param {Object} fieldValueMap The full field value map
 * @return {Object} Filtered field value map
 */
export function filterRequiredData(str, fieldValueMap) {
  // Find all dynamic fields in the string
  const parsed = Mustache.parse(str);

  // Extract unique field names
  const fields = [...new Set(
    parsed
      .filter((token) => token[0] === 'name')
      .map((token) => token[1]),
  )];

  return filterDeep(fieldValueMap, fields);
} // filterRequiredData()

/**
 * Convert columns to filter criteria objects.
 * @param {Array.<Object>} columns Columns to convert
 * @param {Boolean} includeIntegrationColumns Whether to include columns that require integration
 * @returns {Array.<Object>} Array of filter criteria objects
 */
export function getFilterCriteriaFromColumns(columns = [], includeIntegrationColumns = false) {
  return columns
    .filter((column) => column.filterable && (includeIntegrationColumns || !column.requireIntegration))
    .map((column) => ({
      filterType: column.filterType,
      value: column.filterObject
        ? `${column.filterObject}.${column.name}`
        : column.name,
      label: column.selectionLabel || column.label || column.exportLabel || '',
      filterProperty: column.filterProperty || column.field,
      filterPrefix: column.filterPrefix === undefined ? column.label : column.filterPrefix,
      filterObject: column.filterObject || null,
      filterObjectKey: column.filterObjectKey || null,
      filterObjectForeignKey: column.filterObjectForeignKey || null,
      options: {
        filterCriteriaItems: column.filterItems || [],
        arrayProperty: column.filterArrayProperty || null,
        allowNegative: column.filterAllowNegative || false,
        includeFuture: column.filterIncludeFuture || false,
        filterDependency: column.filterDependency || null,
        dateOperators: column.filterDateOperators || false,
        singleValued: column.filterSingleValued || false,
      },
      group: column.group,
      order: column.filterOrder,
      filterPropertyCallback: column.filterPropertyCallback || null,
    }));
} // getFilterCriteriaFromColumns()

/**
 * Remove unsupported runtime-only function fields from filters before saving to DB.
 * @param {Object} filterObject
 * @returns {Object}
 */
export function serializeFilters(filterObject = {}) {
  if (typeof filterObject !== 'object' || filterObject === null) {
    return {};
  }

  const serializedFilters = cloneDeep(filterObject);

  Object.keys(serializedFilters).forEach((filterKey) => {
    const filter = serializedFilters[filterKey];

    if (!filter || typeof filter !== 'object') {
      return;
    }

    Object.keys(filter).forEach((propKey) => {
      if (typeof filter[propKey] === 'function') {
        delete filter[propKey];
      }
    });
  });

  return serializedFilters;
}

/**
 * Re-attach runtime-only function fields to filters after loading from DB.
 * @param {Object} filterObject
 * @param {Array} columns
 * @returns {Object}
 */
export function hydrateFilters(filterObject = {}, columns = []) {
  if (typeof filterObject !== 'object' || filterObject === null) {
    return {};
  }

  const hydratedFilters = cloneDeep(filterObject);

  Object.keys(hydratedFilters).forEach((filterKey) => {
    const matchingColumn = columns.find((column) => {
      const columnFilterKey = column.filterObject
        ? `${column.filterObject}.${column.name}`
        : column.name;

      return columnFilterKey === filterKey;
    });

    if (!matchingColumn) {
      return;
    }

    const filter = hydratedFilters[filterKey];

    if (!filter || typeof filter !== 'object') {
      return;
    }

    Object.keys(matchingColumn).forEach((propKey) => {
      if (typeof matchingColumn[propKey] === 'function') {
        filter[propKey] = matchingColumn[propKey];
      }
    });
  });

  return hydratedFilters;
}
